import type { FC, ReactNode } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { ITextfieldProps } from "@similarweb/ui-components";
import { Button, ButtonLabel, Textfield } from "@similarweb/ui-components";

import { Text } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { ECategoryType } from "app/common/services/categoryService.types";
import { WebsiteListTypeSelectorContainer } from "app/components/website-list-selector/WebsiteListTypeSelectorContainer";
import { hasMarketingPermission } from "app/services/Workspaces.service";

import { ButtonGroup } from "../../ButtonsGroup/src/ButtonsGroup";
import I18n from "../../WithTranslation/src/I18n";

import { ModalTableSelectionContainer, Title, SubTitle, AlertMessage, AlertText } from "./StyledComponents";

interface IModalTableSelectionNewGroupProps {
    title?: string;
    subTitle: string;
    placeholder: string;
    typeLabel?: ReactNode;
    onCancel: () => void;
    onSubmit?: () => void;
    submitButtonDisabled?: boolean;
    onGroupNameChange: (e: FixmeType) => void;
    isLoading: boolean;
    error?: boolean;
    errorMessage?: string;
    groupType?: "website" | "keyword" | "segment";
    selectedListType?: ECategoryType;
    onListTypeSelect?: (type: ECategoryType) => void;
    alertMessage?: string;
}

const TextfieldStyled = styled(Textfield)<ITextfieldProps>`
    margin-bottom: 24px;
`;

const SelectorContainer = styled.div`
    margin-bottom: 24px;
`;

export const ModalTableSelectionNewGroup: FC<IModalTableSelectionNewGroupProps> = ({
    title,
    subTitle,
    placeholder = "",
    typeLabel,
    onCancel,
    onSubmit,
    submitButtonDisabled,
    onGroupNameChange,
    isLoading,
    error,
    errorMessage,
    groupType,
    selectedListType = ECategoryType.GENERAL_LIST,
    onListTypeSelect,
    alertMessage,
}) => {
    return (
        <ModalTableSelectionContainer>
            <Title>
                <Text
                    fontSize={20}
                    fontWeight={500}
                >
                    {title}
                </Text>
            </Title>
            <SubTitle>
                <Text
                    fontSize={14}
                    fontWeight={500}
                >
                    {subTitle}
                </Text>
            </SubTitle>
            {alertMessage ? (
                <AlertMessage>
                    <SWReactIcons
                        iconName="alerts"
                        size="sm"
                    />
                    <AlertText>{alertMessage}</AlertText>
                </AlertMessage>
            ) : null}
            <TextfieldStyled
                error={error}
                errorMessage={errorMessage}
                placeholder={placeholder}
                onChange={onGroupNameChange}
                autoFocus={groupType === "segment"}
                dataAutomation="group-name-text-field"
            />
            {onListTypeSelect && groupType === "website" && hasMarketingPermission() ? (
                <SelectorContainer>
                    <SubTitle>
                        <Text
                            fontSize={14}
                            fontWeight={500}
                        >
                            {typeLabel ? typeLabel : <I18n>table.selection.newgroup.type.website.title</I18n>}
                        </Text>
                    </SubTitle>
                    <WebsiteListTypeSelectorContainer
                        selectedListType={selectedListType}
                        onListTypeSelect={onListTypeSelect}
                        disabled={false}
                    />
                </SelectorContainer>
            ) : null}
            <ButtonGroup>
                <Button
                    type="flat"
                    onClick={onCancel}
                >
                    <ButtonLabel>
                        <I18n>table.selection.newgroup.cancel</I18n>
                    </ButtonLabel>
                </Button>
                <Button
                    type="primary"
                    onClick={onSubmit}
                    isLoading={isLoading}
                    isDisabled={submitButtonDisabled}
                >
                    <ButtonLabel>
                        <I18n>table.selection.newgroup.confirm</I18n>
                    </ButtonLabel>
                </Button>
            </ButtonGroup>
        </ModalTableSelectionContainer>
    );
};
ModalTableSelectionNewGroup.displayName = "ModalTableSelectionNewGroup";
